import { useEffect, useState } from "react";

import { amountRangeLabel } from "@/lib/amount-ranges";
import { capture } from "@/lib/posthog";
import { useContractorPreview } from "@/hooks/queries/useContractorPreview";
import { useStartOnboardingQualification } from "@/hooks/mutations/useOnboardingQualificationMutations";
import { ContractingDetailSheet } from "@/components/contractings/ContractingDetailSheet";
import { BASES_SUBJECTS } from "@/lib/domain-constants";
import type { Department } from "@/types/domain";
import { InlineError } from "../OnboardingShell";
import { OnboardingAlertsStep } from "../OnboardingAlertsStep";
import { ProductStep } from "../ProductStep";
import { ZoneStep } from "../ZoneStep";
import { capitalize, zoneLabel } from "../lineHelpers";
import type { OnboardingSession } from "../useOnboardingSession";
import { useOnboardingUnlock } from "../useOnboardingUnlock";
import { useProductSelection } from "../useProductSelection";
import { ContractorLiveCard } from "./ContractorLiveCard";
import { ContractorResultStep } from "./ContractorResultStep";
import { DocumentsLaterStep } from "./DocumentsLaterStep";
import { DocumentsStep } from "./DocumentsStep";
import { QualificationResultStep } from "./QualificationResultStep";
import { QualifyingStep } from "./QualifyingStep";
import { SizeStep, type AmountRange } from "./SizeStep";
import { SubjectStep } from "./SubjectStep";
import { CONTRACTOR_STAGES, SALE_SUBJECTS, type SaleSubject } from "./contractorLine";

export type ContractorStep =
  | "subject"
  | "works"
  | "zone"
  | "size"
  | "result"
  | "documents"
  | "later"
  | "qualifying"
  | "qualification"
  | "alerts";

/**
 * Flujo de quien le vende al Estado: qué le vende (obras, bienes, servicios o
 * consultoría de obra),
 * qué hace, dónde y sus licitaciones convocadas. Las obras además eligen tamaño
 * y, si suben documentos, ven en cuáles califican (5 cruces de prueba). La línea
 * se guarda al salir de la última pregunta.
 */
export function ContractorFlow({
  session,
  departments,
  onStepChange,
  onExit,
}: {
  session: OnboardingSession;
  departments: Department[];
  onStepChange: (step: ContractorStep) => void;
  /** Vuelve al selector de "¿Cómo vas a usar LicitiGo?". */
  onExit: () => void;
}) {
  const selection = useProductSelection();
  const { unlockingId, unlockWork } = useOnboardingUnlock("contractor");
  const startQualification = useStartOnboardingQualification();

  const [step, setStep] = useState<ContractorStep>("subject");
  const [subject, setSubject] = useState<SaleSubject>("works");
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [range, setRange] = useState<AmountRange | null>(null);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [openWorkId, setOpenWorkId] = useState<number | null>(null);
  // Si alguna salió con veredicto, el dashboard abre con "Analizadas".
  const [landOnAnalyzed, setLandOnAnalyzed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => onStepChange(step), [step]);

  const sale = SALE_SUBJECTS[subject];
  const isWorks = subject === "works";
  // Con bases leídas se le ofrece el cruce de prueba; bienes todavía no.
  const readsBases = BASES_SUBJECTS.includes(subject);
  // Las obras tienen una pregunta más: el tamaño.
  const questions = isWorks ? 3 : 2;
  const asking = step === "works" || step === "zone" || step === "size";
  const previewInput = {
    subjects: sale.lineSubjects,
    terms: selection.terms,
    departmentIds,
    amountMin: range?.min ?? null,
    amountMax: range?.max ?? null,
  };
  const livePreview = useContractorPreview({ ...previewInput, enabled: asking });
  const resultPreview = useContractorPreview({
    ...previewInput,
    details: true,
    enabled: step === "result",
  });

  const zone = zoneLabel(departments, departmentIds);
  const size = amountRangeLabel(range?.min, range?.max);

  /** Cambiar de objeto descarta lo elegido: los términos y el tamaño eran del anterior. */
  function selectSubject(next: SaleSubject) {
    capture("onboarding_subject_selected", { subject: next });
    if (next !== subject) {
      selection.remove(selection.terms);
      setRange(null);
    }
    setSubject(next);
    setStep("works");
  }

  /** Guarda la línea con lo respondido y pasa a sus licitaciones. */
  async function saveContractorLine() {
    setError(null);
    setSaving(true);
    try {
      const id = await session.saveLine({
        name: selection.lineName,
        data: {
          searchprofile_departmentids: departmentIds,
          searchprofile_stages: CONTRACTOR_STAGES,
          searchprofile_subjects: sale.lineSubjects,
          // Lo que hace se busca en los ítems de la licitación (en obras, su título).
          searchprofile_keywords: selection.terms,
          searchprofile_amountmin: range?.min ?? null,
          searchprofile_amountmax: range?.max ?? null,
        },
        resources: [],
      });
      setProfileId(id);
      setStep("result");
    } catch {
      setError("No pudimos guardar tu línea de negocio. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  }

  function calculate() {
    if (profileId == null) return;
    setError(null);
    startQualification.mutate(
      { searchProfileId: profileId },
      {
        onSuccess: () => setStep("qualifying"),
        onError: () => setError("No pudimos arrancar el análisis. Inténtalo de nuevo."),
      },
    );
  }

  // El mismo detalle para las licitaciones del resultado y las de la calificación;
  // desde la calificación abre en sus requisitos.
  const workDetail = (
    <ContractingDetailSheet
      contractingId={openWorkId}
      searchProfileId={profileId ?? undefined}
      open={openWorkId != null}
      onOpenChange={(open) => !open && setOpenWorkId(null)}
      initialTab={step === "qualification" ? "requisitos" : "detalle"}
      hideFullScreen
    />
  );

  if (step === "subject") {
    return <SubjectStep onSelect={selectSubject} onBack={onExit} />;
  }

  if (asking) {
    return (
      // En celular la tarjeta va debajo de la pregunta; en escritorio, a la derecha.
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-12">
        <div>
          {step === "works" && (
            <ProductStep
              segment={isWorks ? "contractor" : "state"}
              tone="blue"
              eyebrow={`Pregunta 1 de ${questions}`}
              title={sale.question}
                            lead={`${sale.starters.length > 0 ? "Elige una o más, o escríbelo" : "Escríbelo"} con tus palabras. Te mostramos solo las licitaciones de tu rubro.`}
              placeholder={sale.placeholder}
              starters={sale.starters}
              termsLabel="La IA buscará en el SEACE"
              products={selection.products}
              onAddProducts={selection.add}
              onRemoveProducts={selection.remove}
              onNext={() => setStep("zone")}
              onBack={() => setStep("subject")}
              onEscape={() => session.escape("contractor", step)}
            />
          )}
          {step === "zone" && (
            <ZoneStep
              tone="blue"
              eyebrow={`Pregunta 2 de ${questions}`}
              title="¿Dónde postulas?"
              lead={sale.zoneLead}
              nextLabel={isWorks ? "Continuar" : `Ver mis ${sale.many}`}
              nextLoading={saving}
              departments={departments}
              departmentIds={departmentIds}
              onChange={setDepartmentIds}
              // El tamaño es solo de obras: lo demás guarda la línea aquí.
              onNext={isWorks ? () => setStep("size") : saveContractorLine}
              onBack={() => setStep("works")}
            />
          )}
          {step === "size" && (
            <SizeStep
              value={range}
              counts={livePreview.data?.ranges.map((bucket) => bucket.works) ?? null}
              allWorks={livePreview.data?.allWorks ?? null}
              saving={saving}
              onChange={setRange}
              onNext={saveContractorLine}
              onBack={() => setStep("zone")}
            />
          )}
          {error && (
            <InlineError
              message={error}
              onEscape={session.configured ? undefined : () => session.escape("contractor", step)}
            />
          )}
        </div>
        <div>
          <ContractorLiveCard
            sale={sale}
            worksLabel={selection.products.length > 0 ? selection.headline : null}
            zone={capitalize(zone)}
            size={isWorks ? size : null}
            preview={livePreview}
          />
        </div>
      </div>
    );
  }

  if (step === "result") {
    return (
      <>
        <ContractorResultStep
          sale={sale}
          rubro={selection.lineName}
          preview={resultPreview.data}
          previewFailed={resultPreview.isError}
          canQualify={readsBases}
          onOpenWork={setOpenWorkId}
          onQualify={() => setStep("documents")}
          onSkip={() => setStep("alerts")}
          onBack={() => setStep(isWorks ? "size" : "zone")}
          onChangeSearch={() => setStep("zone")}
          changeSearchHint={isWorks ? "amplía la zona o el tamaño" : "amplía la zona"}
        />
        {workDetail}
      </>
    );
  }

  if (step === "documents") {
    return (
      <>
        <DocumentsStep
          sale={sale}
          starting={startQualification.isPending}
          onCalculate={calculate}
          onLater={() => setStep("later")}
          onSkip={() => setStep("alerts")}
          onBack={() => setStep("result")}
        />
        {error && <InlineError message={error} />}
      </>
    );
  }

  if (step === "later") {
    return (
      <DocumentsLaterStep onContinue={() => setStep("alerts")} onBack={() => setStep("documents")} />
    );
  }

  if (step === "qualifying") {
    return (
      <QualifyingStep
        sale={sale}
        onDone={() => setStep("qualification")}
        onUploadMore={() => setStep("documents")}
        onSkip={() => setStep("alerts")}
      />
    );
  }

  if (step === "qualification") {
    return (
      <>
        <QualificationResultStep
          sale={sale}
          rubro={selection.lineName.toLowerCase()}
          unlockingId={unlockingId}
          onUnlock={unlockWork}
          onOpenWork={setOpenWorkId}
          onContinue={(anyAnalyzed) => {
            setLandOnAnalyzed(anyAnalyzed);
            setStep("alerts");
          }}
        />
        {workDetail}
      </>
    );
  }

  return (
    <OnboardingAlertsStep
      tone="blue"
      title={`Te avisamos cuando se publique ${isWorks ? "una obra" : "una licitación"} de tu rubro.`}
      isFree={session.isFree}
      onFinish={() => session.finish("contractor", landOnAnalyzed ? "ANALYZED" : null)}
      finishing={session.finishing}
    />
  );
}
